import type { ComponentProps, ReactNode } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Cell, Column, Row, Table, TableBody, TableHeader, VisuallyHidden } from '@launchpad-ui/components';
import clsx from 'clsx';

import styles from './SelectedMetricsTable.module.css';

/** Render-prop signature for a single metric cell, identified by its key. */
export type MetricCellRenderer = (props: { metricKey: string }) => ReactNode;

/** Props for {@link SelectedMetricsTable}. Columns between the metric name and actions are driven by optional render props. */
export interface SelectedMetricsTableProps {
  readonly metricKeys: string[];
  /** Callers supply context-appropriate text (e.g. "Selected metrics", "Monitored metrics"). */
  readonly ariaLabel: string;
  /** Header label for the metric-name column. Defaults to "Metric name". */
  readonly metricColumnLabel?: string;
  /** Header label for the analysis-unit column (when shown). Defaults to "Analysis unit". */
  readonly analysisUnitColumnLabel?: string;
  readonly renderMetricStatus?: MetricCellRenderer;
  readonly renderMetricName: MetricCellRenderer;
  /**
   * When provided, renders the "Analysis unit" cell and shows the column header.
   * Omit to hide the column entirely.
   */
  readonly renderMetricAnalysisUnit?: MetricCellRenderer;
  /**
   * When provided, renders the "Auto rollback" cell and shows the column header.
   * Omit to hide the column entirely (e.g. flows without auto-rollback).
   */
  readonly renderMetricAutoRollback?: MetricCellRenderer;
  /**
   * When provided, renders the "Set as primary" cell and shows the column header.
   * Omit to hide the column entirely (e.g. flows without a primary metric).
   */
  readonly renderMetricPrimary?: MetricCellRenderer;
  readonly renderMetricControls: MetricCellRenderer;
  /** Optional content for the header's actions cell (e.g. a group-level remove button). */
  readonly renderHeaderControls?: () => ReactNode;
  /**
   * react-aria drag-and-drop hooks enabling row reordering. When provided, react-aria injects a
   * drag-handle button into each row and the table switches to the reorderable column layout.
   */
  readonly dragAndDropHooks?: ComponentProps<typeof Table>['dragAndDropHooks'];
  /**
   * Hide the reorder grips while keeping the drag column's width. Use for a table whose rows aren't
   * reorderable but which must stay left-aligned with a sibling reorderable table.
   */
  readonly hideDragHandles?: boolean;
  /**
   * Predicate for which rows expose a reorder grip. Rows that return `false` (e.g. group members
   * after the first) hide their grip while keeping its footprint. Only meaningful with drag hooks.
   */
  readonly isRowReorderable?: (metricKey: string) => boolean;
  /**
   * Predicate for which rows are part of the drag currently in progress. All matching rows dim
   * together — used so dragging a group by its first member dims the whole group, not just that row.
   */
  readonly isRowDragging?: (metricKey: string) => boolean;
  /**
   * When this table is visually stacked with a sibling, squares the adjacent corners and drops the
   * shared border so the two read as one bordered box. 'top' = table above, 'bottom' = table below.
   */
  readonly stackPosition?: 'top' | 'bottom';
  /**
   * Visual treatment. `card` adds a border, rounded corners, and a shaded header; `plain` (default)
   * is the borderless layout. Flows opt into `card` behind a flag so the flag-off look is unchanged.
   */
  readonly appearance?: 'card' | 'plain';
  /** When `false`, renders nothing when `metricKeys` is empty. Defaults to `true`. */
  readonly showEmptyState?: boolean;
}

/**
 * Shared render-prop table shell for a list of configured metrics.
 *
 * Uses `key={JSON.stringify(metricKeys)}` to force a full `<Table>` remount
 * whenever the key list changes, intentionally resetting per-row component
 * state on add/remove — an acceptable tradeoff at the scale of a few metrics.
 */
export function SelectedMetricsTable({
  metricKeys,
  ariaLabel,
  metricColumnLabel = msg('metrics.selectedMetricsTable.metricNameColumn'),
  analysisUnitColumnLabel = msg('metrics.selectedMetricsTable.analysisUnitColumn'),
  renderMetricStatus,
  renderMetricName,
  renderMetricAnalysisUnit,
  renderMetricAutoRollback,
  renderMetricPrimary,
  renderMetricControls,
  renderHeaderControls,
  dragAndDropHooks,
  hideDragHandles,
  isRowReorderable,
  isRowDragging,
  stackPosition,
  appearance = 'plain',
  showEmptyState = true,
}: SelectedMetricsTableProps) {
  if (metricKeys.length === 0) {
    return showEmptyState ? (
      <span className={styles.emptyState}>
        <Message id="metrics.selectedMetricsTable.addMetricsAbove" />
      </span>
    ) : null;
  }

  const hasStatusOverlay = renderMetricStatus !== undefined;
  const showReorder = dragAndDropHooks !== undefined;

  return (
    <Table
      key={JSON.stringify(metricKeys)}
      aria-label={ariaLabel}
      className={clsx(
        styles.table,
        appearance === 'card' && styles.card,
        showReorder && styles.reorderable,
        hideDragHandles && styles.hideDragHandles,
        stackPosition === 'top' && styles.stackTop,
        stackPosition === 'bottom' && styles.stackBottom,
      )}
      dragAndDropHooks={dragAndDropHooks}
    >
      <TableHeader>
        <Column isRowHeader>{metricColumnLabel}</Column>
        {renderMetricAnalysisUnit !== undefined && <Column>{analysisUnitColumnLabel}</Column>}
        {renderMetricAutoRollback !== undefined && (
          <Column>
            <Message id="metrics.selectedMetricsTable.autoRollback" />
          </Column>
        )}
        {renderMetricPrimary !== undefined && (
          <Column className={styles.setPrimaryColumn}>
            <Message id="metrics.selectedMetricsTable.setAsPrimary" />
          </Column>
        )}
        <Column>
          {renderHeaderControls ? (
            renderHeaderControls()
          ) : (
            <VisuallyHidden>
              <Message id="metrics.selectedMetricsTable.actions" />
            </VisuallyHidden>
          )}
        </Column>
      </TableHeader>
      <TableBody
        items={metricKeys.map((metricKey) => ({ id: metricKey, metricKey }))}
        dependencies={[
          renderMetricStatus,
          renderMetricName,
          renderMetricAnalysisUnit,
          renderMetricAutoRollback,
          renderMetricPrimary,
          renderMetricControls,
          isRowReorderable,
          isRowDragging,
          showReorder,
        ]}
      >
        {({ metricKey }) => (
          <Row
            className={clsx(
              showReorder && isRowReorderable && !isRowReorderable(metricKey) && styles.noGrip,
              isRowDragging?.(metricKey) && styles.dragging,
            )}
          >
            <Cell className={styles.metricNameCell}>
              {hasStatusOverlay && <span className={styles.statusOverlay}>{renderMetricStatus({ metricKey })}</span>}
              {renderMetricName({ metricKey })}
            </Cell>
            {renderMetricAnalysisUnit !== undefined && <Cell>{renderMetricAnalysisUnit({ metricKey })}</Cell>}
            {renderMetricAutoRollback !== undefined && (
              <Cell className={styles.centeredCell}>{renderMetricAutoRollback({ metricKey })}</Cell>
            )}
            {renderMetricPrimary !== undefined && (
              <Cell className={clsx(styles.centeredCell, styles.setPrimaryColumn)}>
                {renderMetricPrimary({ metricKey })}
              </Cell>
            )}
            <Cell>{renderMetricControls({ metricKey })}</Cell>
          </Row>
        )}
      </TableBody>
    </Table>
  );
}
